import { type CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import type { AfterViewInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  type OnDestroy,
  signal,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OdlInputEvent } from '@odilo/ui-components';
import type { Competency } from '../../../models/metadata-response';
import type {
  QuestionAnswerType,
  QuizChoice,
  QuizDraft,
  QuizDraftQuestion,
} from '../../../models/quiz';
import { MetadataService } from '../../../services/metadata/metadata';
import { DEFAULT_ANSWER_COUNT, QuizService } from '../../../services/quiz/quiz';
import { ToastService } from '../../../services/toast/toast';
import { SkeletonComponent } from '../../skeleton/skeleton';
import type { QuizAiGenerateEvent } from '../quiz-ai-panel/quiz-ai-panel';
import { QuizAiPanelComponent } from '../quiz-ai-panel/quiz-ai-panel';
import { CompetencyPickerComponent } from '../competency-picker/competency-picker';

const MIN_CHOICES = 2;
const MIN_ATTEMPTS = 2;
const MAX_ATTEMPTS = 100;
const DEFAULT_TIME_MINUTES = 1;
const DEFAULT_NUM_RANDOM_QUESTIONS = 1;

type AiState = 'panel' | 'noContent' | 'generating' | 'skipped';

@Component({
  selector: 'nr-quiz-editor-dialog',
  imports: [
    TranslatePipe,
    DragDropModule,
    QuizAiPanelComponent,
    SkeletonComponent,
    CompetencyPickerComponent,
  ],
  templateUrl: './quiz-editor-dialog.html',
  styleUrl: './quiz-editor-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class QuizEditorDialogComponent implements AfterViewInit, OnDestroy {
  private readonly quizService = inject(QuizService);
  private readonly metadataService = inject(MetadataService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly imagePreviewUrls = new Map<File, string>();

  private stopHeaderScrollShadowSync?: () => void;

  private destroyed = false;

  readonly confirmDelete = signal(false);

  readonly saving = signal(false);

  readonly questionsExpanded = signal(true);

  readonly settingsExpanded = signal(this.metadataService.competencies().length > 0);

  readonly draft = this.quizService.quizDraft;

  readonly competencies = this.metadataService.competencies;

  readonly aiSeedText = this.quizService.aiSeedText();

  readonly aiState = signal<AiState>(
    this.aiSeedText === undefined ? 'skipped' : this.resolveAiEntryState(this.aiSeedText),
  );

  readonly generatedByAi = signal(false);

  readonly addingQuestionWithAi = signal(false);

  readonly canUseAi = this.metadataService.canUseAi;

  readonly canAddAttempts = this.metadataService.canAddAttempts;

  readonly canManageQuestions = this.metadataService.canManageQuestions;

  readonly showAddQuestionAiMenu = computed(() => this.canUseAi() && this.generatedByAi());

  readonly showAiBadge = computed(() => this.aiState() !== 'skipped' || this.generatedByAi());

  readonly quizMode = this.quizService.quizMode();

  readonly dialogTitleKey =
    this.quizMode === 'question' ? 'QUESTIONS.QUESTION_TITLE' : 'QUESTIONS.QUIZ_TITLE';

  readonly deleteDialogTitleKey =
    this.quizMode === 'question'
      ? 'QUESTIONS.DELETE_TITLE_QUESTION'
      : 'QUESTIONS.DELETE_TITLE_QUIZ';

  readonly deleteDialogBodyKey =
    this.quizMode === 'question' ? 'QUESTIONS.DELETE_BODY_QUESTION' : 'QUESTIONS.DELETE_BODY_QUIZ';

  readonly selectedCompetencies = computed(() => this.draft()?.competencies ?? []);

  readonly activeQuestionId = signal<string | null>(
    this.draft()?.questions.length === 1 && !this.draft()?.questions[0].id
      ? (this.draft()?.questions[0].tempId ?? null)
      : null,
  );

  readonly isLocked = computed(() => {
    const draftId = this.draft()?.id;
    if (!draftId) return false;
    const status = this.quizService.quizzes().find(({ quiz }) => quiz.id === draftId);
    return Boolean(status && (status.isLocked || status.quiz.hasAnswers));
  });

  readonly isReadOnly = computed(() => this.isLocked() || !this.canManageQuestions());

  readonly hasTimeLimit = signal((this.draft()?.time ?? 0) > 0);

  readonly hasMultipleAttempts = signal((this.draft()?.maxAttempts ?? 0) > 0);

  readonly numRandomQuestionsCapped = signal(false);

  readonly answerType = signal<QuestionAnswerType>(this.draft()?.answerType ?? 'multipleChoice');

  readonly enableAiRating = signal(this.draft()?.enableAiRating ?? false);

  readonly isOpenEnded = computed(
    () => this.quizMode === 'question' && this.answerType() === 'openEnded',
  );

  // Without AI the option stays visible only so an already-saved AI rating can be turned off.
  readonly showAiRatingOption = computed(() => this.canUseAi() || this.enableAiRating());

  readonly isValid = computed(() => {
    const draft = this.draft();
    if (!draft) return false;
    if (this.quizMode !== 'question' && !draft.name.trim()) return false;
    if (draft.questions.length === 0) return false;
    const openEnded = this.isOpenEnded();
    const questionsValid = draft.questions.every(
      (question) =>
        question.text.trim().length > 0 &&
        (openEnded ||
          (question.choices.length >= MIN_CHOICES &&
            question.choices.every((choice) => choice.text.trim().length > 0))),
    );
    if (!questionsValid) return false;
    if (openEnded && this.enableAiRating() && !(draft.referenceAnswer ?? '').trim()) return false;
    if (this.hasTimeLimit() && !((draft.time ?? 0) > 0)) return false;
    if (!openEnded && this.hasMultipleAttempts() && !((draft.maxAttempts ?? 0) > 0)) return false;
    if (!openEnded && draft.randomQuestions && !((draft.numRandomQuestions ?? 0) > 0)) return false;
    return true;
  });

  constructor() {
    document.addEventListener('click', this.collapseActiveCardOnOutsideClick, true);
  }

  updateName(event: OdlInputEvent): void {
    this.updateDraft((draft) => ({ ...draft, name: event.detail.value }));
  }

  updateQuestionText(questionIndex: number, event: OdlInputEvent): void {
    this.updateQuestion(questionIndex, (question) => ({ ...question, text: event.detail.value }));
  }

  updateChoiceText(questionIndex: number, choiceIndex: number, event: OdlInputEvent): void {
    this.updateQuestion(questionIndex, (question) => ({
      ...question,
      choices: question.choices.map(
        (choice, index): QuizChoice =>
          index === choiceIndex ? { ...choice, text: event.detail.value } : choice,
      ),
    }));
  }

  setCorrectChoice(questionIndex: number, event: OdlInputEvent): void {
    const correctChoice = Number(event.detail.value);
    this.updateQuestion(questionIndex, (question) => ({ ...question, correctChoice }));
  }

  addChoice(questionIndex: number): void {
    this.updateQuestion(questionIndex, (question) => ({
      ...question,
      choices: [...question.choices, { text: '' }],
    }));
  }

  removeChoice(questionIndex: number, choiceIndex: number): void {
    this.updateQuestion(questionIndex, (question) => {
      if (question.choices.length <= MIN_CHOICES) return question;
      const choices = question.choices.filter((_, index) => index !== choiceIndex);
      const correctChoice =
        question.correctChoice >= choiceIndex && question.correctChoice > 0
          ? question.correctChoice - 1
          : question.correctChoice;
      return { ...question, choices, correctChoice: Math.min(correctChoice, choices.length - 1) };
    });
  }

  addQuestion(): void {
    const newQuestion = this.makeEmptyQuestion();
    this.updateDraft((draft) => ({
      ...draft,
      questions: [...draft.questions, newQuestion],
    }));
    this.numRandomQuestionsCapped.set(false);
    this.activeQuestionId.set(newQuestion.tempId);
  }

  removeQuestion(questionIndex: number): void {
    const draft = this.draft();
    const removed = draft?.questions[questionIndex];
    const remaining = (draft?.questions.length ?? 1) - 1;
    const exceedsPool =
      Boolean(draft?.randomQuestions) && (draft?.numRandomQuestions ?? 0) > remaining;
    this.updateDraft((current) => ({
      ...current,
      questions: current.questions.filter((_, index) => index !== questionIndex),
      numRandomQuestions: exceedsPool ? remaining : current.numRandomQuestions,
    }));
    if (exceedsPool) this.numRandomQuestionsCapped.set(true);
    if (removed && this.activeQuestionId() === removed.tempId) this.activeQuestionId.set(null);
  }

  isQuestionEditable(question: QuizDraftQuestion): boolean {
    return question.tempId === this.activeQuestionId();
  }

  expandQuestion(question: QuizDraftQuestion): void {
    this.activeQuestionId.set(question.tempId);
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  deleteChoiceButtonId(question: QuizDraftQuestion, choiceIndex: number): string {
    return `quiz-choice-delete-${question.tempId}-${choiceIndex}`;
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  addChoiceButtonId(question: QuizDraftQuestion): string {
    return `quiz-choice-add-${question.tempId}`;
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  deleteImageButtonId(question: QuizDraftQuestion): string {
    return `quiz-image-delete-${question.tempId}`;
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  questionMenuButtonId(question: QuizDraftQuestion): string {
    return `quiz-question-menu-${question.tempId}`;
  }

  reorderQuestions(event: CdkDragDrop<QuizDraftQuestion[]>): void {
    this.updateDraft((draft) => {
      const questions = [...draft.questions];
      moveItemInArray(questions, event.previousIndex, event.currentIndex);
      return { ...draft, questions };
    });
  }

  toggleQuestionsExpanded(): void {
    this.questionsExpanded.update((expanded) => !expanded);
  }

  toggleSettingsExpanded(): void {
    this.settingsExpanded.update((expanded) => !expanded);
  }

  onImageSelected(questionIndex: number, files: FileList | null): void {
    const file = files?.item(0);
    if (!file) return;
    if (!this.quizService.validateImage(file)) return;
    this.releasePreviewUrl(this.draft()?.questions[questionIndex]);
    const imageUUID = crypto.randomUUID();
    const renamedFile = new File([file], imageUUID, { type: file.type });
    this.updateQuestion(questionIndex, (question) => ({
      ...question,
      image: undefined,
      newImage: renamedFile,
      imageUUID,
      imageRemoved: false,
    }));
  }

  imagePreviewUrl(question: QuizDraftQuestion): string | undefined {
    if (!question.newImage) return question.image;
    let url = this.imagePreviewUrls.get(question.newImage);
    if (!url) {
      url = URL.createObjectURL(question.newImage);
      this.imagePreviewUrls.set(question.newImage, url);
    }
    return url;
  }

  removeImage(questionIndex: number): void {
    const question = this.draft()?.questions[questionIndex];
    this.releasePreviewUrl(question);
    this.updateQuestion(questionIndex, (current) => ({
      ...current,
      image: undefined,
      newImage: undefined,
      imageUUID: undefined,
      imageRemoved: Boolean(current.image),
    }));
  }

  ngAfterViewInit(): void {
    void this.initHeaderScrollShadowSync();
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.collapseActiveCardOnOutsideClick, true);
    this.destroyed = true;
    this.stopHeaderScrollShadowSync?.();
    this.imagePreviewUrls.forEach((url) => {
      URL.revokeObjectURL(url);
    });
    this.imagePreviewUrls.clear();
  }

  // A time limit and multiple attempts are mutually exclusive, as in the previous NubeReader.
  toggleTimeLimit(event: OdlInputEvent<boolean>): void {
    const checked = event.detail.value;
    this.hasTimeLimit.set(checked);
    if (checked) this.hasMultipleAttempts.set(false);
    this.updateDraft((draft) => ({
      ...draft,
      time: checked ? DEFAULT_TIME_MINUTES : undefined,
      applyTimeAllQuizzes: checked ? draft.applyTimeAllQuizzes : undefined,
      maxAttempts: checked ? undefined : draft.maxAttempts,
    }));
  }

  updateTime(event: OdlInputEvent, inputElement: { value: string }): void {
    const time = QuizEditorDialogComponent.sanitizeNonNegativeInput(
      inputElement,
      event.detail.value,
    );
    this.updateDraft((draft) => ({ ...draft, time }));
  }

  toggleApplyTimeAll(event: OdlInputEvent<boolean>): void {
    this.updateDraft((draft) => ({ ...draft, applyTimeAllQuizzes: event.detail.value }));
  }

  toggleRandomQuestions(event: OdlInputEvent<boolean>): void {
    const checked = event.detail.value;
    this.numRandomQuestionsCapped.set(false);
    this.updateDraft((draft) => ({
      ...draft,
      randomQuestions: checked,
      numRandomQuestions: checked ? DEFAULT_NUM_RANDOM_QUESTIONS : undefined,
    }));
  }

  toggleMessyAnswers(event: OdlInputEvent<boolean>): void {
    this.updateDraft((draft) => ({ ...draft, messyAnswers: event.detail.value }));
  }

  updateNumRandomQuestions(event: OdlInputEvent, inputElement: { value: string }): void {
    const poolSize = this.draft()?.questions.length ?? 0;
    const numRandomQuestions = QuizEditorDialogComponent.sanitizeNonNegativeInput(
      inputElement,
      event.detail.value,
      poolSize,
    );
    this.numRandomQuestionsCapped.set((Number(event.detail.value) || 0) > poolSize);
    this.updateDraft((draft) => ({ ...draft, numRandomQuestions }));
  }

  toggleMultipleAttempts(event: OdlInputEvent<boolean>): void {
    const checked = event.detail.value;
    this.hasMultipleAttempts.set(checked);
    if (checked) this.hasTimeLimit.set(false);
    this.updateDraft((draft) => ({
      ...draft,
      maxAttempts: checked ? MIN_ATTEMPTS : undefined,
      time: checked ? undefined : draft.time,
      applyTimeAllQuizzes: checked ? undefined : draft.applyTimeAllQuizzes,
    }));
  }

  updateMaxAttempts(event: OdlInputEvent, inputElement: { value: string }): void {
    const maxAttempts = QuizEditorDialogComponent.sanitizeNonNegativeInput(
      inputElement,
      event.detail.value,
      MAX_ATTEMPTS,
    );
    this.updateDraft((draft) => ({ ...draft, maxAttempts }));
  }

  commitMaxAttempts(inputElement: { value: string }): void {
    const current = Number(inputElement.value) || 0;
    if (current >= MIN_ATTEMPTS) return;
    inputElement.value = MIN_ATTEMPTS.toString();
    this.updateDraft((draft) => ({ ...draft, maxAttempts: MIN_ATTEMPTS }));
  }

  setAnswerType(event: OdlInputEvent): void {
    const { value } = event.detail;
    if (value !== 'openEnded' && value !== 'multipleChoice') return;
    this.answerType.set(value);
    this.updateDraft((draft) => ({ ...draft, answerType: value }));
  }

  toggleAiRating(event: OdlInputEvent<boolean>): void {
    const checked = event.detail.value;
    this.enableAiRating.set(checked);
    this.updateDraft((draft) => ({
      ...draft,
      enableAiRating: checked,
      referenceAnswer: checked ? (draft.referenceAnswer ?? '') : undefined,
    }));
  }

  updateReferenceAnswer(value: string): void {
    this.updateDraft((draft) => ({ ...draft, referenceAnswer: value }));
  }

  updateCompetencies(competencies: Competency[]): void {
    this.updateDraft((draft) => ({ ...draft, competencies }));
  }

  async startAddQuestionWithAi(): Promise<void> {
    if (this.addingQuestionWithAi()) return;
    this.addingQuestionWithAi.set(true);
    try {
      const numberOfAnswers =
        this.draft()?.questions.at(-1)?.choices.length ?? DEFAULT_ANSWER_COUNT;
      const result = await this.quizService.generateQuizWithAi({
        source: 'visible',
        seedText: '',
        questionsNumber: 1,
        numberOfAnswers,
        competencyIds: [],
      });
      if (!result?.questions.length) {
        this.toastService.error(
          String(this.translateService.instant('QUESTIONS.ERROR.AI_GENERATION')),
        );
        return;
      }
      const [newQuestion] = result.questions;
      this.updateDraft((draft) => ({ ...draft, questions: [...draft.questions, newQuestion] }));
      this.numRandomQuestionsCapped.set(false);
      this.activeQuestionId.set(newQuestion.tempId);
    } finally {
      this.addingQuestionWithAi.set(false);
    }
  }

  async onGenerate(options: QuizAiGenerateEvent): Promise<void> {
    this.aiState.set('generating');
    const result = await this.quizService.generateQuizWithAi({
      source: options.source,
      seedText: this.aiSeedText ?? '',
      questionsNumber: options.questionsNumber,
      numberOfAnswers: options.numberOfAnswers,
      competencyIds: options.competencyIds,
    });
    if (!result) {
      this.toastService.error(
        String(this.translateService.instant('QUESTIONS.ERROR.AI_GENERATION')),
      );
      this.aiState.set('panel');
      return;
    }

    const questions =
      options.answerType === 'openEnded'
        ? result.questions.map((question) => ({ ...question, choices: [], correctChoice: -1 }))
        : result.questions;
    const selectedCompetencies = this.competencies().filter((competency) =>
      options.competencyIds.includes(competency._id),
    );
    this.updateDraft((draft) => ({
      ...draft,
      questions,
      competencies: selectedCompetencies,
      answerType: options.answerType,
    }));
    this.answerType.set(options.answerType);
    this.generatedByAi.set(true);
    this.aiState.set('skipped');
    this.activeQuestionId.set(null);
    if (result.partial) {
      this.toastService.warning(
        String(this.translateService.instant('QUESTIONS.AI_PARTIAL_WARNING')),
      );
    }
  }

  cancel(): void {
    if (this.saving()) return;
    this.quizService.cancelQuizDraft();
  }

  async save(): Promise<void> {
    if (!this.isValid() || this.saving() || this.isReadOnly()) return;
    this.saving.set(true);
    try {
      await this.quizService.saveQuizDraft();
    } finally {
      this.saving.set(false);
    }
  }

  async confirmDeleteQuiz(): Promise<void> {
    const draft = this.draft();
    this.confirmDelete.set(false);
    if (!draft?.id) return;
    await this.quizService.deleteQuiz(draft.id, draft.relId);
  }

  private readonly collapseActiveCardOnOutsideClick = (event: MouseEvent): void => {
    if (this.activeQuestionId() === null) return;
    const activeCard = this.elementRef.nativeElement.querySelector<HTMLElement>(
      '.question-card:not(.question-card--readonly)',
    );
    if (!activeCard || event.composedPath().includes(activeCard)) return;
    this.activeQuestionId.set(null);
  };

  private async initHeaderScrollShadowSync(): Promise<void> {
    const dialog = this.elementRef.nativeElement.querySelector<
      HTMLElement & { updateComplete?: Promise<unknown> }
    >('.quiz-editor-dialog');
    await dialog?.updateComplete;
    if (this.destroyed) return;
    this.stopHeaderScrollShadowSync = QuizEditorDialogComponent.syncHeaderScrollShadow(
      this.elementRef.nativeElement,
    );
  }

  private static syncHeaderScrollShadow(host: HTMLElement): (() => void) | undefined {
    const dialog = host.querySelector('.quiz-editor-dialog');
    const scrollable = dialog?.shadowRoot?.querySelector<HTMLElement>('.content');
    const body = dialog?.shadowRoot?.querySelector<HTMLElement>('.body');
    if (!scrollable || !body) return undefined;

    const update = (): void => {
      const isScrolledDown =
        scrollable.scrollHeight - scrollable.clientHeight > 0 && scrollable.scrollTop > 0;
      host.style.setProperty(
        '--quiz-header-border-color',
        isScrolledDown ? 'var(--select-border-color)' : 'transparent',
      );
    };

    update();
    scrollable.addEventListener('scroll', update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(body);

    return () => {
      scrollable.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }

  private resolveAiEntryState(seedText: string): 'panel' | 'noContent' {
    return this.quizService.hasGenerationContent('visible', seedText) ? 'panel' : 'noContent';
  }

  private releasePreviewUrl(question: QuizDraftQuestion | undefined): void {
    if (!question?.newImage) return;
    const url = this.imagePreviewUrls.get(question.newImage);
    if (!url) return;
    URL.revokeObjectURL(url);
    this.imagePreviewUrls.delete(question.newImage);
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  private makeEmptyQuestion(): QuizDraftQuestion {
    return {
      tempId: crypto.randomUUID(),
      text: '',
      choices: [{ text: '' }, { text: '' }, { text: '' }],
      correctChoice: 0,
    };
  }

  private static sanitizeNonNegativeInput(
    inputElement: { value: string },
    rawValue: string,
    max = Infinity,
  ): number {
    const parsed = Number(rawValue) || 0;
    const clamped = Math.min(max, Math.max(0, parsed));
    if (clamped !== parsed) {
      inputElement.value = clamped.toString();
    }
    return clamped;
  }

  private updateDraft(updater: (draft: QuizDraft) => QuizDraft): void {
    const current = this.draft();
    if (!current) return;
    this.quizService.quizDraft.set(updater(current));
  }

  private updateQuestion(
    questionIndex: number,
    updater: (question: QuizDraftQuestion) => QuizDraftQuestion,
  ): void {
    this.updateDraft((draft) => ({
      ...draft,
      questions: draft.questions.map((question, index) =>
        index === questionIndex ? updater(question) : question,
      ),
    }));
  }
}
